import React, { useEffect, useRef, useState } from "react";

import {
  faBug,
  faCalculatorSimple,
  faChevronDown,
  faCog,
  faDungeon,
  faEdit,
  faIndustry,
  faPlus,
  faUpload,
} from "@fortawesome/pro-regular-svg-icons";
import { useVirtualizer } from "@tanstack/react-virtual";
import Dropzone from "react-dropzone";
import { useLocation, useNavigate } from "react-router-dom";

import { ActualVersionsOverview } from "src/adminPanel/ActualVersionsOverview";
import { AddUserModal } from "src/adminPanel/AddUserModal";
import { BootstrapFeatureStoreConfig } from "src/adminPanel/BootstrapFeatureStoreConfig";
import { CreateOrganizationModal } from "src/adminPanel/CreateOrganizationModal";
import { getFeatureGateTitle } from "src/adminPanel/featureGateMetadata";
import { ModifyWsConfigModal } from "src/adminPanel/ModifyWsConfigModal";
import { ModifyWsFeatureGatesModal } from "src/adminPanel/ModifyWsFeatureGatesModal";
import { LifecycleTransitionModal } from "src/adminPanel/modulesV2/LifecycleTransitionModal";
import {
  computeAvailableActions,
  LIFECYCLE_LABEL,
  LIFECYCLE_VARIANT,
} from "src/adminPanel/modulesV2/utils";
import { UploadFeatureStoreData } from "src/adminPanel/UploadFeatureStoreData";
import { WorkspaceDebugModal } from "src/adminPanel/WorkspaceDebugModal";
import { WorkspaceVersionDisplay } from "src/adminPanel/WorkspaceVersionDisplay";
import { Organization } from "src/api/flowTypes";
import {
  useDeleteOrganizationLogo,
  useDeleteWorkspace,
  useUpdateOrg,
  useUpdateOrganizationLogo,
  useUpdateWorkspace,
} from "src/api/queries";
import { Button } from "src/base-components/Button";
import { Checkbox } from "src/base-components/Checkbox";
import { OrgLogo } from "src/base-components/OrgLogo";
import { Pill, PillVariants } from "src/base-components/Pill";
import { LifecycleState, WorkspaceDataplane } from "src/clients/flow-api";
import { OrgPartner } from "src/clients/taktile-api";
import { EmptyState } from "src/design-system/EmptyState";
import { IconButton } from "src/design-system/Icon";
import { useModal } from "src/design-system/Modal";
import { toastActions } from "src/design-system/Toast/utils";
import { PALETTE } from "src/design-system/utils/colorPalette";
import { useEventListener } from "src/hooks/usehooks-ts";
import { WorkspaceFormModal } from "src/layout/WorkspaceFormModal/WorkspaceFormModal";
import { AuthProviders } from "src/userManagement/AuthProviders";
import { errorMessage } from "src/utils/stringUtils";

const HighlightText: React.FC<{ text: string; searchTerm: string }> = ({
  text,
  searchTerm,
}) => {
  if (!searchTerm) return <>{text}</>;

  const parts = text.split(new RegExp(`(${searchTerm})`, "gi"));
  return (
    <>
      {parts.map((part, i) =>
        part.toLowerCase() === searchTerm.toLowerCase() ? (
          <span key={i} className="bg-yellow-200">
            {part}
          </span>
        ) : (
          part
        ),
      )}
    </>
  );
};

export const OrganizationsDisplay: React.FC<{
  org: Organization;
  workspaces: WorkspaceDataplane[];
  handleOrgSelection: (orgId: string) => void;
  handleWsSelection: (wsId: string | null) => void;
  selectedOrg: string | null;
  setAddUserModalIsOpen: (isOpen: boolean) => void;
  setWorkspaceModalIsOpen: (isOpen: boolean) => void;
  filterStatus: string;
  searchTerm: string;
  flowServicesVersionFilter?: string | null;
  filteredActualVersionWorkspaceIds?: string[] | null;
}> = ({
  org,
  workspaces,
  handleOrgSelection,
  handleWsSelection,
  setWorkspaceModalIsOpen,
  setAddUserModalIsOpen,
  filterStatus,
  searchTerm,
  flowServicesVersionFilter,
  filteredActualVersionWorkspaceIds,
}) => {
  const deleteLogo = useDeleteOrganizationLogo();
  const uploadLogo = useUpdateOrganizationLogo();
  const updateOrg = useUpdateOrg();
  const [wsSelected, setWsSelected] = useState<WorkspaceDataplane | null>(null);
  const [wsModalIsOpen, setWsModalIsOpen] = useState(false);
  const [modifyWsFeatureGatesModalIsOpen, setModifyWsFeatureGatesModalIsOpen] =
    useState(false);
  const [
    bootstrapFeatureStoreConfigIsOpen,
    setBootstrapFeatureStoreConfigIsOpen,
  ] = useState(false);
  const [uploadFeatureStoreDataIsOpen, setUploadFeatureStoreDataIsOpen] =
    useState(false);
  const [debugModalIsOpen, setDebugModalIsOpen] = useState(false);
  const [isAltPressed, setIsAltPressed] = useState(false);
  const deleteWorkspace = useDeleteWorkspace();
  const updateWorkspace = useUpdateWorkspace();
  const {
    isOpen: isLifecycleOpen,
    openModal: openLifecycleModal,
    closeModal: closeLifecycleModal,
    afterLeave: afterLifecycleLeave,
    data: lifecycleWorkspace,
  } = useModal<WorkspaceDataplane>();
  // Re-read from the live list so the modal reflects the latest state after a
  // transition is applied, rather than the snapshot captured when it opened.
  const currentLifecycleWorkspace = lifecycleWorkspace
    ? (workspaces.find((ws) => ws.id === lifecycleWorkspace.id) ??
      lifecycleWorkspace)
    : undefined;
  const lifecycleCurrent =
    currentLifecycleWorkspace?.lifecycle_state ?? LifecycleState.ACTIVE;
  const variantLevels = ["gray", "green", "yellow", "red"];
  const getVariant = (level: number) => {
    return variantLevels[
      Math.min(level, variantLevels.length - 1)
    ] as PillVariants;
  };

  const orgWorkspaces = workspaces.filter(
    (ws) => ws.organization_id === org.id,
  );
  const filteredWorkspaces = orgWorkspaces.filter(
    (ws) =>
      (filterStatus === "ALL" || ws.status === filterStatus) &&
      (!flowServicesVersionFilter ||
        ws.flow_services_version === flowServicesVersionFilter) &&
      (!filteredActualVersionWorkspaceIds ||
        filteredActualVersionWorkspaceIds.includes(ws.id)),
  );
  const hiddenCount =
    flowServicesVersionFilter || filteredActualVersionWorkspaceIds
      ? orgWorkspaces.filter(
          (ws) =>
            (filterStatus === "ALL" || ws.status === filterStatus) &&
            ((flowServicesVersionFilter &&
              ws.flow_services_version !== flowServicesVersionFilter) ||
              (filteredActualVersionWorkspaceIds &&
                !filteredActualVersionWorkspaceIds.includes(ws.id))),
        ).length
      : 0;

  useEventListener("keydown", (event) => {
    if (event.altKey) {
      setIsAltPressed(true);
    }
  });

  useEventListener("keyup", (event) => {
    if (!event.altKey) {
      setIsAltPressed(false);
    }
  });

  const handleReactivateOrg = () => {
    if (confirm(`Reactivate organization ${org.name}?`)) {
      updateOrg.mutate({
        organizationId: org.id,
        is_active: true,
      });
    }
  };

  const handleDeleteWorkspace = (id: string) => {
    if (confirm(`Delete workspace?`)) {
      deleteWorkspace.mutate(id);
    }
  };

  // Sets the workspace lifecycle_state directly; the backend keeps the
  // deprecated is_active flag in sync (ACTIVE ⇔ is_active true) and propagates
  // the transition to the workspace's modules.
  const handleLifecycleTransition = async (target: LifecycleState) => {
    if (!currentLifecycleWorkspace) return;
    try {
      await updateWorkspace.mutateAsync({
        id: currentLifecycleWorkspace.id,
        name: currentLifecycleWorkspace.name,
        lifecycle_state: target,
      });
      toastActions.success({
        title: "Lifecycle state updated",
        description: `${currentLifecycleWorkspace.name} → ${LIFECYCLE_LABEL[target]}`,
      });
      closeLifecycleModal();
    } catch (error) {
      toastActions.failure({
        title: "Lifecycle change rejected",
        description: errorMessage(error),
      });
    }
  };

  const handleDeactivateOrg = () => {
    const maxLevel = Math.max(
      ...workspaces
        .filter((ws) => ws.organization_id === org.id)
        .map((ws) => ws.config?.profile_level ?? 0),
    );
    if (maxLevel > 0) {
      toastActions.failure({
        title: "Cannot deactivate organization with active workspaces",
        description:
          "You should set the profile level to 0 for all workspaces first.",
      });
    } else {
      if (confirm(`Deactivate organization ${org.name}?`)) {
        updateOrg.mutate({
          organizationId: org.id,
          is_active: false,
        });
      }
    }
  };

  return (
    <>
      {currentLifecycleWorkspace && (
        <LifecycleTransitionModal
          afterLeave={afterLifecycleLeave}
          confirmDescription={(target) => (
            <>
              This moves the workspace to{" "}
              <strong>{LIFECYCLE_LABEL[target]}</strong> and propagates the
              change to its modules.
            </>
          )}
          current={lifecycleCurrent}
          description={currentLifecycleWorkspace.name}
          desired={lifecycleCurrent}
          isOpen={isLifecycleOpen}
          isPending={updateWorkspace.isPending}
          title="Change lifecycle state"
          onClose={closeLifecycleModal}
          onTransition={handleLifecycleTransition}
        />
      )}
      {wsSelected && wsModalIsOpen && (
        <ModifyWsConfigModal
          isOpen={wsModalIsOpen}
          selectedWs={wsSelected}
          onClose={() => setWsModalIsOpen(false)}
        />
      )}
      {wsSelected && modifyWsFeatureGatesModalIsOpen && (
        <ModifyWsFeatureGatesModal
          isOpen={modifyWsFeatureGatesModalIsOpen}
          selectedWs={wsSelected}
          onClose={() => setModifyWsFeatureGatesModalIsOpen(false)}
        />
      )}
      {wsSelected && bootstrapFeatureStoreConfigIsOpen && (
        <BootstrapFeatureStoreConfig
          isOpen={bootstrapFeatureStoreConfigIsOpen}
          selectedWs={wsSelected}
          onClose={() => setBootstrapFeatureStoreConfigIsOpen(false)}
        />
      )}
      {wsSelected && uploadFeatureStoreDataIsOpen && (
        <UploadFeatureStoreData
          isOpen={uploadFeatureStoreDataIsOpen}
          selectedWs={wsSelected}
          onClose={() => setUploadFeatureStoreDataIsOpen(false)}
        />
      )}
      {wsSelected && debugModalIsOpen && (
        <WorkspaceDebugModal
          isOpen={debugModalIsOpen}
          selectedWs={wsSelected}
          onClose={() => setDebugModalIsOpen(false)}
        />
      )}
      <div
        key={org.id}
        className="mb-4 rounded-lg border border-gray-50 bg-white py-2 shadow-base"
      >
        <div className="flex flex-row items-center justify-between gap-2 px-3 py-2">
          <div className="flex flex-row items-center gap-2">
            <OrgLogo org={org} size="medium" />
            <h3 className="font-inter-semibold-16px">
              <HighlightText searchTerm={searchTerm} text={org.name} />
            </h3>
            {workspaces.some((ws) => ws.organization_id === org.id) && (
              <Pill
                variant={getVariant(
                  Math.max(
                    ...workspaces
                      .filter((ws) => ws.organization_id === org.id)
                      .map((ws) => ws.config?.profile_level ?? 0),
                  ),
                )}
              >
                Max Level:{" "}
                {Math.max(
                  ...workspaces
                    .filter((ws) => ws.organization_id === org.id)
                    .map((ws) => ws.config?.profile_level ?? 0),
                )}
              </Pill>
            )}
            {isAltPressed && (
              <Button size="sm" variant="warning" onClick={handleDeactivateOrg}>
                Deactivate Organization
              </Button>
            )}
            {!org.is_active && (
              <Button
                size="sm"
                variant="secondary"
                onClick={handleReactivateOrg}
              >
                Reactivate Organization
              </Button>
            )}
          </div>
          <div className="flex flex-row items-center gap-2">
            <label className="mr-2 flex flex-row items-center gap-x-1 font-inter-normal-13px">
              loanpro login
              <Checkbox
                checked={org.partner === OrgPartner.LOANPRO}
                disabled={updateOrg.isPending}
                onChange={() => {
                  updateOrg.mutate({
                    organizationId: org.id,
                    partner:
                      org.partner === OrgPartner.LOANPRO
                        ? null
                        : OrgPartner.LOANPRO,
                  });
                }}
              />
            </label>
            <label className="mr-2 flex flex-row items-center gap-x-1 font-inter-normal-13px">
              whitelabel
              <Checkbox
                checked={org.whitelabel}
                disabled={updateOrg.isPending}
                onChange={() => {
                  updateOrg.mutate({
                    organizationId: org.id,
                    whitelabel: !org.whitelabel,
                  });
                }}
              />
            </label>
            <label className="mr-2 flex flex-row items-center gap-x-1 font-inter-normal-13px">
              Allows sign up
              <Checkbox
                checked={org.allows_signup}
                disabled={updateOrg.isPending}
                onChange={() => {
                  updateOrg.mutate({
                    organizationId: org.id,
                    allows_signup: !org.allows_signup,
                  });
                }}
              />
            </label>
            <Dropzone
              onDrop={(acceptedFiles) => {
                const file = acceptedFiles[0];
                if (file) {
                  uploadLogo.mutate({
                    organizationId: org.id,
                    logo: file,
                  });
                }
              }}
            >
              {({ getRootProps, getInputProps }) => (
                <div
                  {...getRootProps({
                    className:
                      "bg-gray-100 border border-dashed rounded-md px-2 py-0.5 cursor-pointer",
                  })}
                >
                  <div className="font-inter-medium-12px">
                    Click or Drop new logo here <input {...getInputProps()} />
                  </div>
                </div>
              )}
            </Dropzone>
            <Button
              loading={deleteLogo.isPending}
              size="sm"
              variant="secondary"
              onClick={() => deleteLogo.mutate({ organizationId: org.id })}
            >
              Delete existing logo
            </Button>
            <Button
              iconLeft={faPlus}
              size="sm"
              variant="secondary"
              onClick={() => {
                handleOrgSelection(org.id);
                setAddUserModalIsOpen(true);
              }}
            >
              Add user
            </Button>
          </div>
        </div>
        <div className="flex flex-row">
          <div className="w-1/3 rounded-md p-2">
            <AuthProviders orgId={org.id} />
            <div className="mt-2 flex flex-row gap-2">
              <Pill
                variant="outlined-gray"
                onClick={() => {
                  navigator.clipboard.writeText(org.id);
                  toastActions.success({
                    title: "Organization ID copied to clipboard",
                    description: org.id,
                  });
                }}
              >
                <label className="font-inter-normal-12px">Org ID:</label>
                <HighlightText
                  searchTerm={searchTerm}
                  text={org.id.substring(0, 8)}
                />
              </Pill>
              <Pill variant="outlined-gray">
                <label className="font-inter-normal-12px">Principal:</label>
                <HighlightText searchTerm={searchTerm} text={org.principal} />
              </Pill>
            </div>
          </div>

          <div className="w-2/3 py-2 pr-4 pl-2">
            <div className="!border !border-gray-100 rounded-lg p-2">
              {workspaces.filter((ws) => ws.organization_id === org.id).length >
              0 ? (
                <div className="mb-1 flex w-full flex-row items-center justify-between px-2">
                  <p className="truncate py-2.5 pr-4 text-left font-inter-medium-12px text-gray-800">
                    Workspaces
                  </p>
                  <Button
                    iconLeft={faPlus}
                    size="sm"
                    variant="secondary"
                    onClick={() => {
                      handleOrgSelection(org.id);
                      handleWsSelection(null);
                      setWorkspaceModalIsOpen(true);
                    }}
                  >
                    Add Workspace
                  </Button>
                </div>
              ) : (
                <></>
              )}
              {(() => {
                return filteredWorkspaces.length > 0 ? (
                  <>
                    {filteredWorkspaces.map((ws) => {
                      const lifecycleState =
                        ws.lifecycle_state ?? LifecycleState.ACTIVE;
                      const lifecycleActions = computeAvailableActions({
                        current: lifecycleState,
                        desired: lifecycleState,
                      });
                      const canChangeLifecycle =
                        lifecycleActions.status === "ready" &&
                        lifecycleActions.targets.length > 0;
                      return (
                        <div
                          key={ws.id}
                          className={`flex w-full flex-col items-center gap-2 px-2 py-1 ${!ws.is_active ? "bg-gray-200" : ""}`}
                        >
                          <div
                            className={`flex w-full flex-row ${
                              ws.status === "FAILURE" ? "bg-orange-100" : ""
                            }`}
                          >
                            <div
                              key={ws.id}
                              className="block flex w-full flex-row items-center truncate text-left font-inter-normal-12px"
                            >
                              <div className="mr-2">
                                <HighlightText
                                  searchTerm={searchTerm}
                                  text={ws.name}
                                />
                              </div>
                              <Pill
                                variant="outlined-gray"
                                onClick={() => {
                                  navigator.clipboard.writeText(ws.id);
                                  toastActions.success({
                                    title: "Copied workspace ID",
                                    description: ws.id,
                                  });
                                }}
                              >
                                <HighlightText
                                  searchTerm={searchTerm}
                                  text={ws.id.substring(0, 8)}
                                />
                              </Pill>
                              <WorkspaceVersionDisplay workspace={ws} />
                              {Object.entries(ws.feature_gates ?? {}).map(
                                ([key, value]) =>
                                  value && (
                                    <div key={key} className="mx-1">
                                      <Pill size="sm" variant="indigo-light">
                                        <span title={key}>
                                          {getFeatureGateTitle(key)}
                                        </span>
                                      </Pill>
                                    </div>
                                  ),
                              )}
                              <div className="ml-2">
                                <Pill
                                  variant={getVariant(
                                    ws.config?.profile_level ?? 0,
                                  )}
                                >
                                  Level: {ws.config?.profile_level}
                                </Pill>
                              </div>
                              {ws.status !== "SUCCESS" && (
                                <Pill variant="orange">{ws.status}</Pill>
                              )}
                            </div>
                            <div className="group relative mr-2">
                              <IconButton
                                icon={faUpload}
                                size="xs"
                                onClick={() => {
                                  setWsSelected(ws);
                                  setUploadFeatureStoreDataIsOpen(true);
                                }}
                              />
                              <span className="absolute -bottom-8 left-1/2 -translate-x-1/2 whitespace-nowrap rounded bg-gray-800 px-2 py-1 font-inter-normal-12px text-white opacity-0 transition-opacity group-hover:opacity-100">
                                Upload Feature Store Data
                              </span>
                            </div>
                            <div className="group relative mr-2">
                              <IconButton
                                icon={faCalculatorSimple}
                                size="xs"
                                onClick={() => {
                                  setWsSelected(ws);
                                  setBootstrapFeatureStoreConfigIsOpen(true);
                                }}
                              />
                              <span className="absolute -bottom-8 left-1/2 -translate-x-1/2 whitespace-nowrap rounded bg-gray-800 px-2 py-1 font-inter-normal-12px text-white opacity-0 transition-opacity group-hover:opacity-100">
                                Bootstrap Feature Store Config
                              </span>
                            </div>
                            <div className="group relative mr-2">
                              <IconButton
                                icon={faEdit}
                                size="xs"
                                onClick={() => {
                                  handleWsSelection(ws.id);
                                  handleOrgSelection(org.id);
                                  setWorkspaceModalIsOpen(true);
                                }}
                              />
                              <span className="absolute -bottom-8 left-1/2 -translate-x-1/2 whitespace-nowrap rounded bg-gray-800 px-2 py-1 font-inter-normal-12px text-white opacity-0 transition-opacity group-hover:opacity-100">
                                Edit Workspace
                              </span>
                            </div>
                            <div className="group relative mr-2">
                              <IconButton
                                icon={faCog}
                                size="xs"
                                onClick={() => {
                                  setWsSelected(ws);
                                  setWsModalIsOpen(true);
                                }}
                              />
                              <span className="absolute -bottom-8 left-1/2 -translate-x-1/2 whitespace-nowrap rounded bg-gray-800 px-2 py-1 font-inter-normal-12px text-white opacity-0 transition-opacity group-hover:opacity-100">
                                Workspace Settings
                              </span>
                            </div>
                            <div className="group relative mr-2">
                              <IconButton
                                icon={faDungeon}
                                size="xs"
                                onClick={() => {
                                  setWsSelected(ws);
                                  setModifyWsFeatureGatesModalIsOpen(true);
                                }}
                              />
                              <span className="absolute -bottom-8 left-1/2 -translate-x-1/2 whitespace-nowrap rounded bg-gray-800 px-2 py-1 font-inter-normal-12px text-white opacity-0 transition-opacity group-hover:opacity-100">
                                Modify Feature Gates
                              </span>
                            </div>
                            <div className="group relative mr-2">
                              <IconButton
                                icon={faBug}
                                size="xs"
                                onClick={() => {
                                  setWsSelected(ws);
                                  setDebugModalIsOpen(true);
                                }}
                              />
                              <span className="absolute -bottom-8 left-1/2 -translate-x-1/2 whitespace-nowrap rounded bg-gray-800 px-2 py-1 font-inter-normal-12px text-white opacity-0 transition-opacity group-hover:opacity-100">
                                Debug Information
                              </span>
                            </div>
                            {!org.is_active && (
                              <Button
                                size="sm"
                                variant="warning"
                                onClick={() => handleDeleteWorkspace(ws.id)}
                              >
                                Delete
                              </Button>
                            )}
                            {canChangeLifecycle ? (
                              <Button
                                iconRight={faChevronDown}
                                size="sm"
                                variant="secondary"
                                onClick={() => openLifecycleModal(ws)}
                              >
                                {LIFECYCLE_LABEL[lifecycleState]}
                              </Button>
                            ) : (
                              <div className="shrink-0 whitespace-nowrap">
                                <Pill
                                  variant={LIFECYCLE_VARIANT[lifecycleState]}
                                >
                                  {LIFECYCLE_LABEL[lifecycleState]}
                                </Pill>
                              </div>
                            )}
                          </div>
                          <div className="h-px w-full rounded-full bg-gray-100"></div>
                        </div>
                      );
                    })}
                    {hiddenCount > 0 && (
                      <div className="px-2 py-1 font-inter-normal-12px text-gray-500 italic">
                        +{hiddenCount} workspaces hidden by version filter
                      </div>
                    )}
                  </>
                ) : (
                  <div className="h-60">
                    <EmptyState
                      actionButton={{
                        icon: faPlus,
                        label: "Add Workspace",
                        onClick: () => {
                          handleOrgSelection(org.id);
                          handleWsSelection(null);
                          setWorkspaceModalIsOpen(true);
                        },
                      }}
                      dataLoc="admin-org-no-workspaces"
                      description="There are currently no workspaces available for this organization!"
                      headline="No Workspaces available"
                      icon={faIndustry}
                    ></EmptyState>
                  </div>
                );
              })()}
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

const FlowServicesVersionBar: React.FC<{
  workspaces: WorkspaceDataplane[];
  selectedVersion: string | null;
  onSelectVersion: (version: string | null) => void;
  allVersions?: string[]; // optional, for consistent color mapping
}> = ({ workspaces, selectedVersion, onSelectVersion, allVersions }) => {
  // Count workspaces per version
  const versionCounts: Record<string, number> = {};
  workspaces.forEach((ws) => {
    if (ws.flow_services_version) {
      versionCounts[ws.flow_services_version] =
        (versionCounts[ws.flow_services_version] || 0) + 1;
    }
  });
  const total = Object.values(versionCounts).reduce((a, b) => a + b, 0);
  // Use allVersions for the canonical order if provided, else fall back to
  // the keys of versionCounts. Bar segments are derived from the same list so
  // they always match the legend pills below.
  const colorVersions = allVersions || Object.keys(versionCounts);
  const versions = colorVersions.filter((v) => versionCounts[v] > 0);
  const colors = PALETTE;
  // Map version to color index based on allVersions
  const versionColorIndex: Record<string, number> = {};
  colorVersions.forEach((v, i) => {
    versionColorIndex[v] = i;
  });

  // For each version, count failed workspaces
  const versionFailureCounts: Record<string, number> = {};
  workspaces.forEach((ws) => {
    if (ws.flow_services_version && ws.status === "FAILURE") {
      versionFailureCounts[ws.flow_services_version] =
        (versionFailureCounts[ws.flow_services_version] || 0) + 1;
    }
  });

  return (
    <div className="mb-4 w-full rounded-lg border border-gray-200 p-4">
      <h3 className="mb-2 font-inter-semibold-14px text-gray-900">
        Desired Versions Overview
      </h3>
      <div className="flex h-6 w-full overflow-hidden rounded border border-gray-200">
        {versions.map((version) => {
          const totalCount = versionCounts[version];
          const failCount = versionFailureCounts[version] || 0;
          const okCount = totalCount - failCount;
          const color = colors[versionColorIndex[version] % colors.length];
          const widthPercent = (totalCount / total) * 100;
          const failPercent =
            failCount > 0 ? (failCount / totalCount) * 100 : 0;
          const okPercent = 100 - failPercent;
          return (
            <div
              key={version}
              className="h-full"
              style={{
                width: `${widthPercent}%`,
                position: "relative",
                height: "100%",
              }}
            >
              {/* Normal (ok) part */}
              <button
                aria-pressed={selectedVersion === version}
                className="h-full transition-all duration-300 focus:outline-none"
                style={{
                  width: "100%",
                  height: "100%",
                  background: color,
                  border: "none",
                  borderRadius: 0,
                  cursor: "pointer",
                  outline:
                    selectedVersion === version ? "2px solid #222" : undefined,
                  outlineOffset: "-2px",
                  position: "absolute",
                  left: 0,
                  top: 0,
                  zIndex: 1,
                  // If there are failures, only fill the ok part
                  clipPath:
                    failCount > 0
                      ? `inset(0 ${100 - okPercent}% 0 0)`
                      : undefined,
                }}
                title={`${version}: ${okCount} healthy workspaces`}
                type="button"
                onClick={() => onSelectVersion(version)}
              />
              {/* Failure (dashed/orange) part */}
              {failCount > 0 && (
                <button
                  aria-pressed={selectedVersion === version}
                  className="h-full transition-all duration-300 focus:outline-none"
                  style={{
                    width: "100%",
                    height: "100%",
                    background: `repeating-linear-gradient(135deg, ${color}, ${color} 6px, #fff 6px, #fff 12px)`,
                    border: "none",
                    borderRadius: 0,
                    cursor: "pointer",
                    outline:
                      selectedVersion === version
                        ? "2px solid #222"
                        : undefined,
                    outlineOffset: "-2px",
                    position: "absolute",
                    left: 0,
                    top: 0,
                    zIndex: 2,
                    opacity: 1,
                    clipPath: `inset(0 0 0 ${okPercent}%)`,
                  }}
                  title={`${version}: ${failCount} failed workspaces`}
                  type="button"
                  onClick={() => onSelectVersion(version)}
                />
              )}
            </div>
          );
        })}
      </div>
      <div className="mt-2 flex flex-wrap items-center gap-2">
        {selectedVersion
          ? colorVersions
              .filter(
                (version) =>
                  version === selectedVersion && versionCounts[version] > 0,
              )
              .map((version) => (
                <button
                  key={version}
                  aria-pressed={true}
                  className="flex items-center gap-1 rounded bg-indigo-50 px-2 py-1 font-inter-normal-12px ring-2 ring-indigo-500"
                  style={{ cursor: "pointer" }}
                  type="button"
                  onClick={() => onSelectVersion(version)}
                >
                  <span
                    className="inline-block h-3 w-3 rounded"
                    style={{
                      background:
                        colors[versionColorIndex[version] % colors.length],
                    }}
                  />
                  <span className="font-mono">{version}</span>
                  <span className="text-gray-500">
                    ({versionCounts[version] || 0})
                  </span>
                </button>
              ))
          : colorVersions
              .filter((version) => versionCounts[version] > 0)
              .map((version) => (
                <button
                  key={version}
                  aria-pressed={selectedVersion === version}
                  className={`flex items-center gap-1 rounded px-2 py-1 font-inter-normal-12px ${selectedVersion === version ? "bg-indigo-50 ring-2 ring-indigo-500" : ""}`}
                  style={{ cursor: "pointer" }}
                  type="button"
                  onClick={() => onSelectVersion(version)}
                >
                  <span
                    className="inline-block h-3 w-3 rounded"
                    style={{
                      background:
                        colors[versionColorIndex[version] % colors.length],
                    }}
                  />
                  <span className="font-mono">{version}</span>
                  <span className="text-gray-500">
                    ({versionCounts[version] || 0})
                  </span>
                </button>
              ))}
        {selectedVersion && (
          <button
            className="ml-2 font-inter-normal-12px text-gray-600 underline"
            type="button"
            onClick={() => onSelectVersion(null)}
          >
            Clear filter
          </button>
        )}
      </div>
    </div>
  );
};

export const OrganizationsWorkspacesSettings: React.FC<{
  organizations: Organization[];
  workspaces: WorkspaceDataplane[];
  isActive: boolean;
}> = ({ organizations, workspaces, isActive }) => {
  const location = useLocation();
  const navigate = useNavigate();
  // Get search param from URL
  const params = new URLSearchParams(location.search);
  const urlSearch = params.get("search") || "";
  const [searchTerm, setSearchTerm] = useState<string>(urlSearch);

  // Sync searchTerm with URL param
  useEffect(() => {
    if (urlSearch !== searchTerm) {
      setSearchTerm(urlSearch);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [urlSearch]);

  // Update URL when searchTerm changes via input
  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSearchTerm(e.target.value);
    const newParams = new URLSearchParams(location.search);
    if (e.target.value) {
      newParams.set("search", e.target.value);
    } else {
      newParams.delete("search");
    }
    navigate({ search: newParams.toString() }, { replace: true });
  };

  const [selectedOrg, setSelectedOrg] = useState<string | null>(null);
  const [selectedWs, setSelectedWs] = useState<string | null>(null);
  const [workspaceModalIsOpen, setWorkspaceModalIsOpen] = useState(false);
  const [orgModalIsOpen, setOrgModalIsOpen] = useState(false);
  const [addUserModalIsOpen, setAddUserModalIsOpen] = useState(false);
  const [filterStatus, setFilterStatus] = useState<string>("ALL");
  const [sortOption, setSortOption] = useState<string>("NAME_ASC");
  const [includeWithoutWorkspaces, setIncludeWithoutWorkspaces] =
    useState<boolean>(isActive);
  const [flowServicesVersionFilter, setFlowServicesVersionFilter] = useState<
    string | null
  >(null);
  const [actualVersionFilter, setActualVersionFilter] = useState<string | null>(
    null,
  );
  const [
    filteredActualVersionWorkspaceIds,
    setFilteredActualVersionWorkspaceIds,
  ] = useState<string[] | null>(null);

  const parentRef = useRef<HTMLDivElement>(null);
  const searchInputRef = useRef<HTMLInputElement>(null);

  const workspaceStatusCounts = {
    ALL: workspaces.length,
    SUCCESS: workspaces.filter((ws) => ws.status === "SUCCESS").length,
    FAILURE: workspaces.filter((ws) => ws.status === "FAILURE").length,
  };

  const filteredAndSortedOrganizations = [...organizations]
    .filter(
      (org) =>
        org.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
        org.principal?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        org.id.startsWith(searchTerm) ||
        workspaces.some(
          (ws) =>
            ws.organization_id === org.id &&
            (ws.id.startsWith(searchTerm) ||
              ws.name.toLowerCase().includes(searchTerm.toLowerCase())),
        ),
    )
    .filter((org) => {
      if (flowServicesVersionFilter) {
        // Only include orgs with at least one workspace matching the version filter
        return workspaces.some(
          (ws) =>
            ws.organization_id === org.id &&
            ws.flow_services_version === flowServicesVersionFilter &&
            isActive === ws.is_active,
        );
      }

      if (actualVersionFilter && filteredActualVersionWorkspaceIds) {
        // Only include orgs that have workspaces in the filtered actual version list
        return workspaces.some(
          (ws) =>
            ws.organization_id === org.id &&
            filteredActualVersionWorkspaceIds.includes(ws.id) &&
            isActive === ws.is_active,
        );
      }

      // If no version filter, use includeWithoutWorkspaces logic
      return includeWithoutWorkspaces
        ? true
        : workspaces.some((ws) => ws.organization_id === org.id);
    })
    .sort((a, b) => {
      switch (sortOption) {
        case "NAME_ASC":
          return a.name.localeCompare(b.name);
        case "NAME_DESC":
          return b.name.localeCompare(a.name);
        case "WORKSPACE_COUNT_ASC":
          return (
            workspaces.filter((ws) => ws.organization_id === a.id).length -
            workspaces.filter((ws) => ws.organization_id === b.id).length
          );
        case "WORKSPACE_COUNT_DESC":
          return (
            workspaces.filter((ws) => ws.organization_id === b.id).length -
            workspaces.filter((ws) => ws.organization_id === a.id).length
          );
        default:
          return 0;
      }
    });

  const orgsWithoutWorkspacesCount = organizations.filter(
    (org) => !workspaces.some((ws) => ws.organization_id === org.id),
  ).length;

  const filteredWorkspaces = workspaces.filter(
    (ws) =>
      isActive === ws.is_active &&
      (!flowServicesVersionFilter ||
        ws.flow_services_version === flowServicesVersionFilter),
  );

  // Compute all unique versions for color mapping, sorted with a numeric-aware
  // comparator so "1.10.0" sorts after "1.2.0" (not before, as a lexicographic
  // sort would do). Bar segments and legend pills both consume this list, so a
  // single canonical order keeps them aligned.
  const allFlowServicesVersions = Array.from(
    new Set(
      workspaces
        .map((ws) => ws.flow_services_version)
        .filter((v): v is string => typeof v === "string" && v.length > 0),
    ),
  ).sort((a, b) => a.localeCompare(b, undefined, { numeric: true }));

  const rowVirtualizer = useVirtualizer({
    count: filteredAndSortedOrganizations.length,
    getScrollElement: () => parentRef.current,
    estimateSize: () => 200,
    overscan: 5,
    measureElement: (element) => {
      return element.getBoundingClientRect().height;
    },
  });

  return (
    <>
      <FlowServicesVersionBar
        allVersions={allFlowServicesVersions}
        selectedVersion={flowServicesVersionFilter}
        workspaces={filteredWorkspaces}
        onSelectVersion={setFlowServicesVersionFilter}
      />
      <ActualVersionsOverview
        allVersions={allFlowServicesVersions}
        selectedVersion={actualVersionFilter}
        workspaces={filteredWorkspaces}
        onFilteredWorkspacesChange={(workspaceIds) => {
          setFilteredActualVersionWorkspaceIds(workspaceIds);
          rowVirtualizer.measure();
        }}
        onSelectVersion={setActualVersionFilter}
      />
      <div className="mb-4 flex items-center gap-4">
        <input
          ref={searchInputRef}
          className="rounded border p-2 text-base"
          placeholder="Search organizations..."
          style={{ minWidth: 300 }}
          type="text"
          value={searchTerm}
          onChange={handleSearchChange}
        />
        <select
          className="rounded border p-1"
          value={filterStatus}
          onChange={(e) => setFilterStatus(e.target.value)}
        >
          <option value="ALL">All ({workspaceStatusCounts.ALL})</option>
          <option value="SUCCESS">
            Success ({workspaceStatusCounts.SUCCESS})
          </option>
          <option value="FAILURE">
            Failure ({workspaceStatusCounts.FAILURE})
          </option>
        </select>

        <select
          className="rounded border p-1"
          value={sortOption}
          onChange={(e) => setSortOption(e.target.value)}
        >
          <option value="NAME_ASC">Organization Name (A-Z)</option>
          <option value="NAME_DESC">Organization Name (Z-A)</option>
          <option value="WORKSPACE_COUNT_ASC">
            Workspace Count (Low-High)
          </option>
          <option value="WORKSPACE_COUNT_DESC">
            Workspace Count (High-Low)
          </option>
        </select>
        <label className="flex flex-row items-center gap-x-1 font-inter-normal-13px">
          <Checkbox
            checked={includeWithoutWorkspaces}
            onChange={() =>
              setIncludeWithoutWorkspaces(!includeWithoutWorkspaces)
            }
          />
          Show organizations without workspaces ({orgsWithoutWorkspacesCount})
        </label>
        {isActive && (
          <Button onClick={() => setOrgModalIsOpen(true)}>
            Create an Organisation
          </Button>
        )}
      </div>
      <div ref={parentRef} className="h-[calc(100vh-200px)] overflow-auto">
        <div
          style={{
            height: `${rowVirtualizer.getTotalSize()}px`,
            width: "100%",
            position: "relative",
          }}
        >
          {rowVirtualizer.getVirtualItems().map((virtualRow) => {
            const org = filteredAndSortedOrganizations[virtualRow.index];
            const orgWorkspaces = workspaces.filter(
              (ws) => ws.organization_id === org.id,
            );
            const filteredOrgWorkspaces = orgWorkspaces.filter(
              (ws) => filterStatus === "ALL" || ws.status === filterStatus,
            );

            if (filterStatus !== "ALL" && filteredOrgWorkspaces.length === 0) {
              return null;
            }

            return (
              <div
                key={`${org.id}-${searchTerm}`}
                ref={rowVirtualizer.measureElement}
                data-index={virtualRow.index}
                style={{
                  position: "absolute",
                  top: 0,
                  left: 0,
                  width: "100%",
                  transform: `translateY(${virtualRow.start}px)`,
                }}
              >
                <OrganizationsDisplay
                  filterStatus={filterStatus}
                  filteredActualVersionWorkspaceIds={
                    filteredActualVersionWorkspaceIds
                  }
                  flowServicesVersionFilter={flowServicesVersionFilter}
                  handleOrgSelection={setSelectedOrg}
                  handleWsSelection={setSelectedWs}
                  org={org}
                  searchTerm={searchTerm}
                  selectedOrg={selectedOrg}
                  setAddUserModalIsOpen={setAddUserModalIsOpen}
                  setWorkspaceModalIsOpen={setWorkspaceModalIsOpen}
                  workspaces={workspaces}
                />
              </div>
            );
          })}
        </div>
      </div>
      {workspaceModalIsOpen && !!selectedOrg && (
        <>
          <WorkspaceFormModal
            isOpen={workspaceModalIsOpen && !!selectedOrg && !selectedWs}
            orgId={selectedOrg || ""}
            variant="Create"
            onClose={() => setWorkspaceModalIsOpen(false)}
          />
          <WorkspaceFormModal
            isOpen={workspaceModalIsOpen && !!selectedOrg && !!selectedWs}
            orgId={selectedOrg || ""}
            variant="Edit"
            wsId={selectedWs || ""}
            onClose={() => setWorkspaceModalIsOpen(false)}
          />
        </>
      )}
      <CreateOrganizationModal
        isOpen={orgModalIsOpen}
        onClose={() => setOrgModalIsOpen(false)}
      />
      <AddUserModal
        isOpen={addUserModalIsOpen}
        selectedOrg={selectedOrg ?? ""}
        onClose={() => setAddUserModalIsOpen(false)}
      />
    </>
  );
};
